import { useCallback, useRef, type RefObject } from 'react';
import { useStepHotkeys } from '../use-step-hotkeys.js';
import { pickTurnStepTargetIndex } from './pick-turn-step-target.js';
import { turnAnchorId } from './turn-nav.js';
import type { ChatItem } from './types.js';

/**
 * `alt+↑` / `alt+↓` step through the transcript a turn at a time — the jump
 * readers make most, and the one the rail is slowest at because it means
 * finding the row first.
 *
 * Steps through every mounted turn, not just the ones the rail lists: the rail
 * is bounded because a hundred rows are unreadable, which is no reason to make
 * the keyboard skip them. Start/end sentinels stay out of the sequence — they
 * are rail jump targets only. Including the zero-height start anchor made the
 * first down-press at the top a no-op. At the real top of a session, setup
 * chrome sits above turn 1 while the rail already highlights it — counting
 * that on-screen turn as current is what lets the first down-press reach turn 2.
 */
export function useTurnStepping({
  containerRef,
  messages,
  enabled,
  onBeforeStep,
}: {
  containerRef: RefObject<HTMLElement | null>;
  messages: ChatItem[];
  enabled: boolean;
  /**
   * Runs with the anchor the step is heading for, before the viewport moves:
   * the transcript releases its scroll pin here, and the rail marks the turn
   * active. Both have to happen first — the pin would pull the view straight
   * back, and a highlight set afterwards would be overwritten by the
   * scroll-driven tracking the jump itself sets off.
   */
  onBeforeStep?: (targetId: string) => void;
}): void {
  const lastTargetIdRef = useRef<string | null>(null);

  const step = useCallback(
    (direction: -1 | 1) => {
      const container = containerRef.current;
      if (!container) return;

      const anchors = messages
        .filter((m) => m.role === 'user')
        .map((m) => turnAnchorId(m.id))
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => el !== null);
      if (anchors.length === 0) return;

      const containerTop = container.getBoundingClientRect().top;
      const offsets = anchors.map((el) => el.getBoundingClientRect().top - containerTop);
      const lastTargetIndex = anchors.findIndex((el) => el.id === lastTargetIdRef.current);

      const targetIndex = pickTurnStepTargetIndex({
        offsets,
        direction,
        lastTargetIndex,
        viewportHeight: container.clientHeight,
      });
      const target = targetIndex >= 0 ? anchors[targetIndex] : undefined;
      if (!target) return;

      onBeforeStep?.(target.id);
      lastTargetIdRef.current = target.id;
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
      target.focus({ preventScroll: true });
    },
    [containerRef, messages, onBeforeStep],
  );

  useStepHotkeys({
    enabled,
    onPrevious: useCallback(() => step(-1), [step]),
    onNext: useCallback(() => step(1), [step]),
  });
}
